import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import { Plus, CalendarDays, X as XIcon } from "lucide-react";

const STATUS_COLORS = {
  Pending: "bg-amber-100 text-amber-700 border-amber-200",
  Approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Rejected: "bg-rose-100 text-rose-700 border-rose-200",
  Cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

export default function MyLeaves() {
  const [items, setItems] = useState([]);
  const [balances, setBalances] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type_id: "", start_date: "", end_date: "", reason: "" });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, b, c] = await Promise.all([
      api.get("/leaves"),
      api.get("/leave-balances"),
      api.get("/leave-types"),
    ]);
    setItems(a.data);
    setBalances(b.data);
    setLeaveTypes(c.data);
  };

  useEffect(() => {
    load();
  }, []);

  const ltMap = useMemo(
    () => Object.fromEntries(leaveTypes.map((l) => [l.id, l])),
    [leaveTypes]
  );

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      await api.post("/leaves", form);
      setOpen(false);
      setForm({ leave_type_id: "", start_date: "", end_date: "", reason: "" });
      load();
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (id) => {
    if (!confirm("Cancel this leave request?")) return;
    await api.patch(`/leaves/${id}/cancel`);
    load();
  };

  const filteredBalances = useMemo(
    () => balances.filter((b) => ltMap[b.leave_type_id]),
    [balances, ltMap]
  );

  return (
    <AdminLayout title="My Leaves" breadcrumbs={["Home", "Self Service", "Leaves"]}>
      {/* Balance cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {filteredBalances.map((b) => {
            const lt = ltMap[b.leave_type_id];
            const remaining = (b.allocated || 0) - (b.used || 0);
            return (
              <div
                key={b.id}
                className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden"
                data-testid={`balance-${lt.code}`}
              >
                <div className="h-1.5" style={{ backgroundColor: lt.color }} />
                <div className="p-4">
                  <div className="text-xs text-slate-500 uppercase font-semibold tracking-wider">
                    {lt.name}
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-slate-800">{remaining}</span>
                    <span className="text-xs text-slate-500">/ {b.allocated} days</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    Used: {b.used} · Year: {b.year}
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-[#226DB4]" /> My Leave Requests
          </h3>
          <button
            data-testid="apply-leave-btn"
            onClick={() => setOpen(true)}
            className="inline-flex items-center gap-2 bg-[#089949] hover:bg-[#067A3A] text-white px-4 py-2 text-sm rounded font-medium"
          >
            <Plus className="h-4 w-4" /> Apply Leave
          </button>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Type</th>
              <th className="text-left px-4 py-3 font-semibold">From</th>
              <th className="text-left px-4 py-3 font-semibold">To</th>
              <th className="text-left px-4 py-3 font-semibold">Days</th>
              <th className="text-left px-4 py-3 font-semibold">Reason</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-slate-400" data-testid="my-leaves-empty">
                  No leave requests yet
                </td>
              </tr>
            ) : (
              items.map((l) => {
                const lt = ltMap[l.leave_type_id];
                return (
                  <tr key={l.id} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <span
                        className="text-[11px] px-2 py-1 rounded font-medium"
                        style={{
                          backgroundColor: (lt?.color || "#226DB4") + "20",
                          color: lt?.color || "#226DB4",
                        }}
                      >
                        {lt?.code || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatDisplayDate(l.start_date)}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDisplayDate(l.end_date)}</td>
                    <td className="px-4 py-3 text-slate-600">{l.days}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{l.reason || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-[11px] px-2 py-1 rounded border font-semibold ${STATUS_COLORS[l.status]}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {(l.status === "Pending" || l.status === "Approved") ? (
                        <button
                          onClick={() => cancel(l.id)}
                          data-testid={`cancel-leave-${l.id}`}
                          className="px-2.5 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1"
                        >
                          <XIcon className="h-3 w-3" /> Cancel
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-800">Apply for Leave</h3>
            <select
              required
              value={form.leave_type_id}
              onChange={(e) => setForm({ ...form, leave_type_id: e.target.value })}
              className={inputCls}
              data-testid="apply-leave-type-select"
            >
              <option value="">Select leave type</option>
              {leaveTypes.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <input
                required
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className={inputCls}
                data-testid="apply-leave-start-input"
              />
              <input
                required
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className={inputCls}
                data-testid="apply-leave-end-input"
              />
            </div>
            <textarea
              rows={3}
              placeholder="Reason (optional)"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className={inputCls}
            />
            {err && <div className="text-sm text-rose-600">{err}</div>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded">
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded disabled:opacity-70"
                data-testid="apply-leave-submit-btn"
              >
                {saving ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
